// Widget chatbot global.
//
// Persistant : rendu par la coquille, il reste ouvert avec sa conversation
// d'une navigation Inertia à l'autre. L'historique est relu à chaque
// ouverture.
//
// L'app native (mobile/src/app.ts) lit .chatbot-window.visible,
// .chatbot-input-wrapper, .chatbot-messages et .chatbot-input, et écrit des
// styles inline sur .chatbot-window : React ne gère donc aucun `style` sur ces
// nœuds (la hauteur du redimensionnement s'écrit par ref).
//
// La barre du bas (bouton IA) l'ouvre et le ferme par l'événement window
// `northvane:chatbot:toggle`.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { t } from '@/lib/i18n'
import ChatMessages from '@/components/chatbot/ChatMessages'
import { isWebviewMode, railsJson, texts } from '@/components/chatbot/format'
import type { ChatbotData } from '@/components/chatbot/types'
import { useChatbotKeyboard, useChatbotScrollLock } from '@/hooks/useChatbotKeyboard'
import { autoResizeTextarea, useChatbotConversation } from '@/hooks/useChatbotConversation'
import { CHATBOT_TOGGLE_EVENT } from '@/components/shell/BottomNav'

const MIN_HEIGHT = 500
const MAX_HEIGHT = 900

export default function Chatbot({ data }: { data: Record<string, any> }) {
  const { hasSubscription, remainingRequests, threshold, showNewDot, subscriptionUrl } = data as ChatbotData

  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const windowRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const openTimerRef = useRef<number | null>(null)
  const stopResizeRef = useRef<(() => void) | null>(null)

  const scrollToBottom = useCallback(() => {
    const el = messagesRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  const updateKeyboardInset = useChatbotKeyboard(() => {
    if (openRef.current) scrollToBottom()
  })
  const { lock, unlock } = useChatbotScrollLock()

  const close = useCallback(() => {
    openRef.current = false
    setOpen(false)
    inputRef.current?.blur()
    unlock(true)
  }, [unlock])

  const convo = useChatbotConversation({
    hasSubscription,
    subscriptionUrl,
    remainingRequests,
    inputRef,
    onClose: close,
  })
  const loadHistoryRef = useRef(convo.loadHistory)
  loadHistoryRef.current = convo.loadHistory

  const openWidget = useCallback(() => {
    openRef.current = true
    setOpen(true)
    // Verrou avant le focus différé : le défilement de la page est déjà figé
    // au premier tap.
    lock()
    updateKeyboardInset()
    if (openTimerRef.current) window.clearTimeout(openTimerRef.current)
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null
      if (inputRef.current && !isWebviewMode()) inputRef.current.focus()
      void loadHistoryRef.current()
    }, 100)
  }, [lock, updateKeyboardInset])

  const toggle = useCallback(() => {
    if (openRef.current) close()
    else openWidget()
  }, [close, openWidget])

  // Bouton IA de la barre du bas
  useEffect(() => {
    const onToggle = () => toggle()
    window.addEventListener(CHATBOT_TOGGLE_EVENT, onToggle)
    return () => window.removeEventListener(CHATBOT_TOGGLE_EVENT, onToggle)
  }, [toggle])

  useEffect(() => {
    autoResizeTextarea(inputRef.current)
    return () => {
      if (openTimerRef.current) window.clearTimeout(openTimerRef.current)
      stopResizeRef.current?.()
    }
  }, [])

  // Chaque ajout (bulle, carte, indicateur, historique) ramène en bas.
  const loading = convo.loadingText !== null
  useLayoutEffect(() => {
    scrollToBottom()
  }, [convo.items, loading, scrollToBottom])

  // Entrée envoie, Maj+Entrée va à la ligne ; dans l'app, Entrée va toujours
  // à la ligne (seul le bouton envoie).
  const handleKeydown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isWebviewMode()) return
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      void convo.sendMessage()
    }
  }

  // Redimensionnement par le haut (bureau ; caché en plein écran par le CSS).
  const startResize = (event: MouseEvent<HTMLDivElement>) => {
    const win = windowRef.current
    if (!win) return
    event.preventDefault()
    event.stopPropagation()
    const startY = event.clientY
    const startHeight = win.offsetHeight
    const onMove = (e: globalThis.MouseEvent) => {
      const height = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startHeight + (startY - e.clientY)))
      win.style.height = `${height}px`
    }
    const stop = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', stop)
      stopResizeRef.current = null
    }
    stopResizeRef.current?.()
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', stop)
    stopResizeRef.current = stop
  }

  const showBadge = convo.remaining <= threshold

  return (
    <div
      data-onboarding="chatbot"
      className="chatbot-widget-container"
      data-typing-text={texts.typing()}
      data-error-text={texts.error()}
      data-network-error-text={texts.networkError()}
      data-just-now-text={texts.justNow()}
      data-subscription-url={subscriptionUrl}
      data-loading-messages={railsJson(texts.loadingMessages())}
      data-humor-loading-messages={railsJson(texts.humorMessages())}
      data-timeout-message={texts.timeout()}
      data-welcome-greeting={t('chatbot.widget.welcome_greeting')}
      data-welcome-item-1={t('chatbot.widget.welcome_item_1')}
      data-welcome-item-2={t('chatbot.widget.welcome_item_2')}
      data-welcome-item-3={t('chatbot.widget.welcome_item_3')}
      data-welcome-item-4={t('chatbot.widget.welcome_item_4')}
      data-welcome-item-5={t('chatbot.widget.welcome_item_5')}
      data-welcome-item-6={t('chatbot.widget.welcome_item_6')}
      data-welcome-footer={t('chatbot.widget.welcome_footer')}
    >
      <button
        type="button"
        className={`chatbot-toggle-btn${open ? ' active' : ''}`}
        aria-label={t('chatbot.widget.toggle_button_aria')}
        onClick={toggle}
      >
        <i className="fas fa-robot"></i>
        {hasSubscription ? (
          <span className={`chatbot-badge${showBadge ? '' : ' hidden'}`} id="remaining-requests" data-threshold={threshold}>
            {convo.remaining}
          </span>
        ) : (
          <span className="chatbot-badge badge-locked">
            <i className="fas fa-lock"></i>
          </span>
        )}
        {showNewDot && <span className="new-page-dot new-page-dot-chatbot" id="chatbot-new-dot"></span>}
      </button>

      <div ref={windowRef} className={`chatbot-window ${open ? 'visible' : 'hidden'}`} role="dialog" aria-label={t('chatbot.widget.window_aria')}>
        <div className="chatbot-window-header">
          <div className="chatbot-window-title">
            <i className="fas fa-robot"></i>{' '}
            <span>{t('chatbot.widget.title')}</span>{' '}
            {hasSubscription ? (
              <span
                className={`badge bg-info ms-2 requests-badge${showBadge ? '' : ' hidden'}`}
                id="requests-badge-header"
                data-threshold={threshold}
              >
                {/* Nombre du serveur entouré de retours à la ligne (sans effet à
                    l'écran), nombre nu une fois réécrit par le client */}
                <span id="remaining-requests-header">{convo.remainingFromClient ? convo.remaining : `\n${convo.remaining}\n`}</span>{' '}
                {t('chatbot.widget.requests_left')}
              </span>
            ) : (
              <span className="badge bg-warning ms-2">
                <i className="fas fa-lock"></i> {t('chatbot.widget.subscription_required.badge')}
              </span>
            )}
          </div>
          <button type="button" onClick={close} className="chatbot-close-btn" aria-label={t('chatbot.widget.close_button_aria')}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div ref={messagesRef} className="chatbot-messages">
          {hasSubscription ? (
            <ChatMessages
              items={convo.items}
              showWelcome={convo.showWelcome}
              loadingText={convo.loadingText}
              onRetry={convo.retryMessage}
              onExecute={convo.executeSystemAction}
            />
          ) : (
            <div className="subscription-required-message">
              <div className="upgrade-notice">
                <i className="fas fa-lock"></i>
                <p>{t('chatbot.widget.subscription_required.simple_message')}</p>
                <a href={subscriptionUrl} className="btn btn-primary upgrade-btn">
                  {t('chatbot.widget.subscription_required.upgrade_now')}
                </a>
              </div>
            </div>
          )}
        </div>

        {hasSubscription ? (
          <div className="chatbot-input-wrapper">
            {/* Pas d'input-group / form-control / btn Bootstrap : voir
                chatbot_widget.css (hauteur 44 px, coins, couleur au tap). */}
            <div className="chatbot-form">
              <div className="chatbot-composer">
                <textarea
                  ref={inputRef}
                  className="chatbot-input"
                  placeholder={t('chatbot.widget.input_placeholder')}
                  autoComplete="off"
                  rows={1}
                  onKeyDown={handleKeydown}
                  onInput={() => autoResizeTextarea(inputRef.current)}
                ></textarea>
                <button type="button" className="chatbot-send-btn" disabled={convo.sendDisabled} onClick={() => void convo.sendMessage()}>
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="chatbot-input-wrapper disabled">
            <div className="upgrade-prompt">
              <i className="fas fa-lock"></i>{' '}
              <span>{t('chatbot.widget.subscription_required.input_prompt')}</span>{' '}
              <a href={subscriptionUrl} className="btn btn-sm btn-primary upgrade-link">
                {t('chatbot.widget.subscription_required.upgrade_now')}
              </a>
            </div>
          </div>
        )}

        <div onMouseDown={startResize} className="chatbot-resize-handle" aria-label={t('chatbot.widget.resize_handle_aria')}>
          <i className="fas fa-grip-lines"></i>
        </div>
      </div>
    </div>
  )
}
